import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Pause, Play, Square, Users, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { uploadSegment, finalizeMeeting, audioExt } from "@/lib/meetingUpload";
import ConsentNotice from "@/components/ConsentNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SEG_MS = 5 * 60 * 1000;
const fmt = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function MeetingRecorder({ onClose }) {
  const navigate = useNavigate();
  const [consent, setConsent] = useState(false);
  const [title, setTitle] = useState("");
  const [phase, setPhase] = useState("setup");
  const [secs, setSecs] = useState(0);
  const [paused, setPaused] = useState(false);
  const [sent, setSent] = useState(0);
  const r = useRef({ stream: null, rec: null, seg: 0, mid: null, queue: Promise.resolve(), stopping: false, lock: null });

  const enqueue = (blob, seg) => {
    r.current.queue = r.current.queue.then(() => uploadSegment(r.current.mid, seg, blob, audioExt(blob.type)).then(() => setSent((n) => n + 1)))
      .catch(() => toast.error(`Envoi du segment ${seg + 1} impossible, il sera retenté à la fin`));
  };

  const newRecorder = () => {
    const mr = new MediaRecorder(r.current.stream);
    const chunks = [];
    const seg = r.current.seg++;
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = () => {
      const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
      if (blob.size) enqueue(blob, seg);
      if (!r.current.stopping) newRecorder();
    };
    mr.start(1000);
    r.current.rec = mr;
  };

  useEffect(() => {
    if (phase !== "recording") return;
    const t = setInterval(() => { if (r.current.rec?.state === "recording") setSecs((s) => s + 1); }, 1000);
    const rot = setInterval(() => { if (r.current.rec?.state === "recording") r.current.rec.stop(); }, SEG_MS);
    return () => { clearInterval(t); clearInterval(rot); };
  }, [phase]);

  useEffect(() => { window.dispatchEvent(new CustomEvent("birdie:close")); }, []);
  useEffect(() => () => { r.current.stopping = true; r.current.stream?.getTracks().forEach((t) => t.stop()); r.current.lock?.release?.(); }, []);

  const start = async () => {
    try {
      r.current.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const { data } = await api.post("/meetings", { kind: "meeting", title });
      r.current.mid = data.id;
      try { r.current.lock = await navigator.wakeLock?.request("screen"); } catch { /* optional */ }
      newRecorder();
      setPhase("recording");
    } catch { toast.error("Micro inaccessible : autorisez-le dans votre navigateur"); }
  };

  const togglePause = () => {
    const mr = r.current.rec;
    if (!mr) return;
    if (mr.state === "recording") { mr.pause(); setPaused(true); } else { mr.resume(); setPaused(false); }
  };

  const stop = async () => {
    setPhase("finishing");
    r.current.stopping = true;
    await new Promise((res) => { const mr = r.current.rec; mr.addEventListener("stop", () => setTimeout(res, 50), { once: true }); mr.stop(); });
    r.current.stream.getTracks().forEach((t) => t.stop());
    r.current.lock?.release?.();
    try {
      await r.current.queue;
      await finalizeMeeting(r.current.mid, secs);
      navigate(`/reunions/${r.current.mid}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Finalisation impossible, réessayez");
      setPhase("recording");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-[#0A0F1D]/95 backdrop-blur-xl flex items-center justify-center p-4" data-testid="meeting-recorder">
      <div className="w-full max-w-md klt-glow-card rounded-3xl p-6 relative text-white">
        {phase === "setup" && <button onClick={onClose} data-testid="meeting-recorder-close" className="absolute top-4 right-4 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>}
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#60A5FA] flex items-center gap-1.5"><Users className="w-4 h-4" /> Réunion · sans limite de durée</p>
        {phase === "setup" ? (
          <div className="space-y-4 mt-3">
            <h2 className="font-heading text-2xl font-extrabold">Enregistrer une réunion</h2>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sujet (facultatif, l'IA le trouvera)" data-testid="meeting-title-input" className="h-11 rounded-xl" />
            <ConsentNotice checked={consent} onChange={setConsent} testid="meeting-consent" />
            <p className="text-xs text-slate-400">Laissez l'écran allumé. L'audio est envoyé par morceaux pendant la réunion : le compte rendu est prêt quelques secondes après la fin, même pour 2 h.</p>
            <Button onClick={start} disabled={!consent} data-testid="meeting-start-btn" className="w-full h-12 rounded-2xl klt-fab-neon font-semibold">Démarrer l'enregistrement</Button>
          </div>
        ) : (
          <div className="text-center mt-6">
            <div className={`mx-auto w-28 h-28 rounded-full flex items-center justify-center ${paused ? "bg-slate-600" : "bg-rose-500 klt-rec-pulse"}`}>
              {phase === "finishing" ? <Loader2 className="w-10 h-10 animate-spin" /> : <span className="w-6 h-6 rounded-full bg-white" />}
            </div>
            <p className="font-heading text-4xl font-extrabold mt-5 tabular-nums" data-testid="meeting-timer">{fmt(secs)}</p>
            <p className="text-sm text-slate-400 mt-1" data-testid="meeting-status">
              {phase === "finishing" ? "Envoi de la fin de l'enregistrement…" : paused ? "En pause" : `Enregistrement en cours · ${sent} morceau${sent > 1 ? "x" : ""} déjà envoyé${sent > 1 ? "s" : ""}`}
            </p>
            {phase === "recording" && (
              <div className="flex gap-3 mt-6">
                <Button variant="secondary" onClick={togglePause} data-testid="meeting-pause-btn" className="flex-1 h-12 rounded-2xl">{paused ? <><Play className="w-4 h-4 mr-1" /> Reprendre</> : <><Pause className="w-4 h-4 mr-1" /> Pause</>}</Button>
                <Button onClick={stop} data-testid="meeting-stop-btn" className="flex-1 h-12 rounded-2xl bg-rose-500 hover:bg-rose-600"><Square className="w-4 h-4 mr-1" fill="currentColor" /> Terminer</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
